import { useEffect, useState } from 'react'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined'
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined'
import { useTranslation } from 'react-i18next'
import {
  getVapidPublicKey,
  sendTestPush,
  subscribePush,
  unsubscribePush,
  urlBase64ToUint8Array,
} from '../shared/api/push'

type Status = 'unsupported' | 'insecure' | 'blocked' | 'enabled' | 'disabled'

/**
 * The Push API is gated on a **secure context** (https, or localhost/127.0.0.1). Over plain HTTP —
 * a phone opening the dev server at `http://192.168.x.x:5173` — the browser simply doesn't define
 * `navigator.serviceWorker`, which is indistinguishable from "this browser can't do push" unless
 * we ask. Checking first turns "Order alerts aren't supported on this browser" (wrong, and sends
 * you debugging the phone) into "needs an https connection" (right, and sends you to the URL).
 */
const insecureContext = () => typeof window !== 'undefined' && !window.isSecureContext

const pushSupported = () =>
  typeof navigator !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window

/** Reject after `ms` so a step that never settles (e.g. SW activation) can't hang "Working…" forever. */
function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => setTimeout(() => reject(new Error(`${label} timed out`)), ms)),
  ])
}

/** True if an existing subscription was created with the same VAPID key we're about to use. */
function sameApplicationServerKey(sub: PushSubscription, key: Uint8Array): boolean {
  const existing = sub.options.applicationServerKey
  if (!existing) {
    return false
  }
  const bytes = new Uint8Array(existing)
  return bytes.length === key.length && bytes.every((b, i) => b === key[i])
}

/**
 * "Enable order alerts" control for the live board (Phase 7d). Web Push lets the owner get a
 * system notification for a new order even when the site/browser is closed — a complement to the
 * SSE board (which only delivers while a tab is open). Subscribing must happen from a user gesture
 * (browser requirement): on click we ask for Notification permission, register the service worker,
 * subscribe via PushManager with the server's VAPID key, and POST the subscription server-side.
 */
export default function OrderAlertsToggle() {
  const { t } = useTranslation()
  // Order matters: an insecure context hides `serviceWorker` entirely, so check it before
  // concluding the browser is at fault.
  const [status, setStatus] = useState<Status>(() =>
    insecureContext() ? 'insecure' : pushSupported() ? 'disabled' : 'unsupported',
  )
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(false)
  const [testMsg, setTestMsg] = useState<string | null>(null)

  // Reflect any existing subscription / blocked permission when the board mounts.
  useEffect(() => {
    if (!pushSupported()) {
      return
    }
    if (Notification.permission === 'denied') {
      setStatus('blocked')
      return
    }
    let cancelled = false
    void navigator.serviceWorker.getRegistration().then(async (registration) => {
      const subscription = await registration?.pushManager.getSubscription()
      if (!cancelled && subscription) {
        setStatus('enabled')
      }
    })
    return () => {
      cancelled = true
    }
  }, [])

  const enable = async () => {
    setError(false)
    setBusy(true)
    try {
      // One overall timeout around the whole flow — pushManager.subscribe()/getSubscription() can
      // hang contacting the push service (FCM) with no built-in timeout, which would freeze the
      // button on "Working…". The race guarantees the toggle always settles.
      await withTimeout(
        (async () => {
          const permission = await Notification.requestPermission()
          if (permission !== 'granted') {
            setStatus(permission === 'denied' ? 'blocked' : 'disabled')
            return
          }
          const registration = await navigator.serviceWorker.register('/sw.js')
          await navigator.serviceWorker.ready
          const vapidKey = await getVapidPublicKey()
          const applicationServerKey = urlBase64ToUint8Array(vapidKey)

          // Reuse an existing subscription only if it matches the current VAPID key. A stale sub
          // from a different key (common in dev when the keypair changes) makes subscribe() throw
          // InvalidStateError and would silently reject our pushes — so drop it and re-subscribe.
          let subscription = await registration.pushManager.getSubscription()
          if (subscription && !sameApplicationServerKey(subscription, applicationServerKey)) {
            await subscription.unsubscribe()
            subscription = null
          }
          if (!subscription) {
            subscription = await registration.pushManager.subscribe({
              userVisibleOnly: true,
              applicationServerKey,
            })
          }
          await subscribePush(subscription)
          setStatus('enabled')
        })(),
        20000,
        'enable order alerts',
      )
    } catch (e) {
      // Surface the real cause — subscribe() can fail/hang for push-service/network reasons (or the
      // POST can fail) and a swallowed error is undiagnosable.
      console.error('[order-alerts] enable failed:', e)
      setError(true)
    } finally {
      setBusy(false)
    }
  }

  const disable = async () => {
    setError(false)
    setBusy(true)
    try {
      await withTimeout(
        (async () => {
          const registration = await navigator.serviceWorker.getRegistration()
          const subscription = await registration?.pushManager.getSubscription()
          if (subscription) {
            await unsubscribePush(subscription.endpoint)
            await subscription.unsubscribe()
          }
          setStatus('disabled')
        })(),
        20000,
        'disable order alerts',
      )
    } catch (e) {
      console.error('[order-alerts] disable failed:', e)
      setError(true)
    } finally {
      setBusy(false)
    }
  }

  // Push fails silently by nature (a dropped message looks exactly like a quiet shop), so the
  // owner needs a way to prove alerts still reach this device without waiting for a real order.
  const test = async () => {
    setTestMsg(null)
    try {
      const sent = await sendTestPush()
      setTestMsg(t('owner.board.alerts.testSent', { count: sent }))
    } catch {
      setTestMsg(t('owner.board.alerts.testError'))
    }
  }

  if (status === 'unsupported' || status === 'insecure' || status === 'blocked') {
    return (
      <Typography variant="body2" color="text.secondary">
        {t(`owner.board.alerts.${status}`)}
      </Typography>
    )
  }

  const on = status === 'enabled'
  return (
    <Stack spacing={0.25} alignItems="flex-end">
      <Stack direction="row" spacing={0.5} alignItems="center">
        {on && (
          <Button size="small" variant="text" onClick={test}>
            {t('owner.board.alerts.test')}
          </Button>
        )}
        <Tooltip title={on ? t('owner.board.alerts.turnOff') : t('owner.board.alerts.hint')}>
          <Button
            variant={on ? 'outlined' : 'contained'}
            color={on ? 'success' : 'primary'}
            size="small"
            disabled={busy}
            startIcon={on ? <NotificationsActiveOutlinedIcon /> : <NotificationsNoneOutlinedIcon />}
            onClick={on ? disable : enable}
          >
            {busy
              ? t('owner.board.alerts.working')
              : on
                ? t('owner.board.alerts.on')
                : t('owner.board.alerts.enable')}
          </Button>
        </Tooltip>
      </Stack>
      {error && (
        <Typography variant="caption" color="error">
          {t('owner.board.alerts.error')}
        </Typography>
      )}
      {testMsg && (
        <Typography variant="caption" color="text.secondary">
          {testMsg}
        </Typography>
      )}
    </Stack>
  )
}
